import React, { useCallback, useEffect, useState } from "react";
import { isEmpty, isEqual } from "lodash";
import { createStructuredSelector } from "reselect";
import { connect } from "react-redux";

import { required, requiredCodeRef, getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { useModelCodeLib } from "utils/codes/modelCodeLib";
import { getOptions, getBaseUnits, getUnitOptions } from "utils/fields";
import { useStateSelector } from "utils/field";
import { extractPrebuiltEcmCodes } from "utils/codes";
import { change } from "store/root/actions";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Tooltip from "components/Tooltip";
import Checkbox from "components/Input/Checkbox";

import { actions } from "features/Model/Enclosure/_ducks";

import Add from "assets/images/icons/JSX/Add";
import Book from "assets/images/icons/JSX/Book";
import Edit from "assets/images/icons/JSX/Edit";

import classes from "./style.module.scss";
import globalStyles from "components/globalStyles.module.scss";
import { isOrgCodeRef } from "utils/codes/refs";

const { setInitCode, updateCodeDrawer } = actions;

const ceilingTypeValidation = [required, ...getValidation("ceilingInsType")];
const constructionTypeValidation = getValidation("ceilingConstructionType");

export const CodeFooter = ({ toggleDrawer, setInitCode, codeType }) => (
    <div
        className={classes.codeFooter}
        onClick={async () => {
            await setInitCode({}, codeType);
            toggleDrawer(true);
        }}
    >
        <span>Create New Code</span>
        <Add />
    </div>
);

const codeSort = (a, b) => {
    if (a.label < b.label) {
        return -1;
    }
    if (a.label > b.label) {
        return 1;
    }
    return 0;
};

const LabelActionComponent = ({ codeRef, handleEdit, codeType }) => (
    <>
        <div
            className={globalStyles.editCode}
            data-tip="Edit Code"
            data-for={codeRef}
            onClick={(event) => handleEdit(event, codeRef, codeType)}
        >
            <Edit />
            <Tooltip id={codeRef} className={globalStyles.fieldTooltip} place="left" />
        </div>
    </>
);

// Ceiling (non-flat) Ins. Type codes found in code library
export const ceilingTypeLibCodesMap = (libCeilingCodes, handleEditLibCeilingCode) =>
    Object.keys(libCeilingCodes)
        .map((codeRef) => {
            const { label = "" } = libCeilingCodes[codeRef] || {};
            const [standardCode] = codeRef.split("Ceiling-U"); //TODO: why is this 'U'?

            return {
                label: label,
                labelIcon: Book,
                labelTag: isOrgCodeRef(codeRef) ? "Organization" : !standardCode ? "User Defined" : "",
                labelActionComponent: () =>
                    handleEditLibCeilingCode ? (
                        <LabelActionComponent
                            codeRef={codeRef}
                            handleEdit={handleEditLibCeilingCode}
                            codeType={standardCode ? "S" : "U"}
                        />
                    ) : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

// Ceiling (non-flat) Ins. Type codes found in model, filtered by those in library
export const ceilingTypeModelCodesMap = (modelCodes, libCeilingCodes, handleEditModelCeilingCode) =>
    Object.values(modelCodes)
        .filter(({ codeRef }) => {
            const [component] = codeRef.split("-");
            if (!!libCeilingCodes[codeRef]) {
                return (
                    (!libCeilingCodes[codeRef] && component === "Ceiling") ||
                    !isEqual(modelCodes[codeRef].layers, libCeilingCodes[codeRef].layers)
                );
            }
            return !libCeilingCodes[codeRef] && component === "Ceiling";
        })
        .map(({ label, codeRef }) => {
            const [standardCode] = codeRef.split("Ceiling-U");

            return {
                label,
                labelTag: isOrgCodeRef(codeRef) ? "Organization" : !standardCode ? "User Defined" : "",
                labelActionComponent: () => (
                    <LabelActionComponent
                        codeRef={codeRef}
                        handleEdit={handleEditModelCeilingCode}
                        codeType={standardCode ? "S" : "U"}
                    />
                ),
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

// CeilingFlat Ins. Type codes found in code library
export const ceilingFlatTypeLibCodesMap = (libCeilingFlatCodes, handleEditLibCeilingFlatCode) =>
    Object.keys(libCeilingFlatCodes)
        .map((codeRef) => {
            const { label = "" } = libCeilingFlatCodes[codeRef] || {};
            const [standardCode] = codeRef.split("CeilingFlat-U"); //TODO: why is this 'U'?

            return {
                label: label,
                labelIcon: Book,
                labelTag: isOrgCodeRef(codeRef) ? "Organization" : !standardCode ? "User Defined" : "",
                labelActionComponent: () =>
                    handleEditLibCeilingFlatCode ? (
                        <LabelActionComponent
                            codeRef={codeRef}
                            handleEdit={handleEditLibCeilingFlatCode}
                            codeType={standardCode ? "S" : "U"}
                        />
                    ) : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

// CeilingFlat Ins. Type codes found in model, filtered by those in library
export const ceilingFlatTypeModelCodesMap = (modelCodes, libCeilingFlatCodes, handleEditModelCeilingFlatCode) =>
    Object.values(modelCodes)
        .filter(({ codeRef }) => {
            const [component] = codeRef.split("-");
            if (!!libCeilingFlatCodes[codeRef]) {
                return (
                    (!libCeilingFlatCodes[codeRef] && component === "CeilingFlat") ||
                    !isEqual(modelCodes[codeRef].layers, libCeilingFlatCodes[codeRef].layers)
                );
            }
            return !libCeilingFlatCodes[codeRef] && component === "CeilingFlat";
        })
        .map(({ label, codeRef }) => {
            const [standardCode] = codeRef.split("CeilingFlat-U");

            return {
                label,
                labelTag: isOrgCodeRef(codeRef) ? "Organization" : !standardCode ? "User Defined" : "",
                labelActionComponent: () => (
                    <LabelActionComponent
                        codeRef={codeRef}
                        handleEdit={handleEditModelCeilingFlatCode}
                        codeType={standardCode ? "S" : "U"}
                    />
                ),
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

// Prebuilt CeilingFlat Ins. Type codes
export const ceilingTypePrebuiltCodesMap = (prebuiltCeilingCodes, handleEditPrebuiltCeilingCode) =>
    Object.keys(prebuiltCeilingCodes)
        .map((codeRef) => {
            const { label = "" } = prebuiltCeilingCodes[codeRef] || {};
            const [standardCode] = codeRef.split("Ceiling-U"); //TODO: why is this 'U'?

            return {
                label: label,
                labelIcon: Add,
                labelTag: isOrgCodeRef(codeRef) ? "Organization" : !standardCode ? "User Defined" : "",
                labelActionComponent: () =>
                    handleEditPrebuiltCeilingCode ? (
                        <LabelActionComponent
                            codeRef={codeRef}
                            handleEdit={handleEditPrebuiltCeilingCode}
                            codeType={standardCode ? "S" : "U"}
                        />
                    ) : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

const Construction = React.memo(
    ({
        accessor,
        componentAccessor = accessor,
        change,
        id,
        setInitCode,
        updateCodeDrawer,
        disabledCodeEdit = false,
        isUpgrade = false,
        canBeEditedFromParametric = true,
        isCompact = false,
        selectedUpgrade,
        isUpgradeLibrary = false,
        upgradeType, // "cathedral" or "ceilingWithAttic"
        isParametricAnalysis = false,
        isCommunity = false,
        codeRefs = [],
        ...props
    }) => {
        const [isTouched, setIsTouched] = useState(false); // Hide warning when code is reselected but not yet added to ecmInputMap

        const { Ceiling: { codes: libCeilingCodes = {} } = {}, CeilingFlat: { codes: libCeilingFlatCodes = {} } = {} } =
            useModelCodeLib() || {};
        const { calculatingRValue = {} } = useStateSelector("enclosure") || {};
        const { currentPackage = {} } = useStateSelector("upgradeLibrary") || {};
        const { modelData = {} } = useStateSelector(`model`) || {};
        const { parametricAnalysis = {} } = useStateSelector(`parametricAnalysis`) || {};
        const { baseModelId = "" } = useStateSelector(`commUpgrades`) || {};
        const { community = {} } = useStateSelector(`community`) || {};

        const ecmInputMap = parametricAnalysis?.ecmInputMap || community?.ecmInputMap || {};

        const allEcmInputMapUpgradesArr = ecmInputMap
            ? Object.keys(ecmInputMap)
                  .map((key) =>
                      Object.entries(ecmInputMap[key])
                          .filter(([k, _]) => k !== "None")
                          .map(([k, v]) => ({ [k]: v }))
                  )
                  .flat()
            : [];

        const allEcmInputMapUpgrades = allEcmInputMapUpgradesArr.reduce((acc, curr) => ({ ...acc, ...curr }), {});

        const addUserSpecifiedRVal = useStateSelector(`${accessor}.addUserSpecifiedRVal`) || false;

        const modelCodes =
            allEcmInputMapUpgrades[selectedUpgrade]?.packageValues?.codes ||
            currentPackage?.upgrades?.[selectedUpgrade]?.codes ||
            modelData?.codes ||
            {};

        const isAddUserSpecifiedRVal = isParametricAnalysis ? addUserSpecifiedRVal : false;

        //Attempt to grab construction type from upgrade package
        let { id: constTypeIdFromPackage = 0 } =
            currentPackage?.upgrades?.[selectedUpgrade]?.fields?.constructionType || {};

        if (upgradeType === "cathedral" && constTypeIdFromPackage === 0) {
            //We're dealing with a brand new upgrade for a cathedral ceiling, apply its default here
            constTypeIdFromPackage = 2;
        }

        //Attempt to grab construction type from ecmInputMap
        let { id: constTypeIdFromParametric = 0 } =
            allEcmInputMapUpgrades[selectedUpgrade]?.packageValues?.fields?.constructionType || {};

        if (upgradeType === "cathedral" && isParametricAnalysis && constTypeIdFromParametric === 0) {
            //We're dealing with a brand new parametric ecm for a cathedral ceiling, apply its default here
            constTypeIdFromParametric = 2;
        }

        const constTypeIdFromCommunity = upgradeType === "cathedral" ? 2 : 0;

        const modelUnits = modelData?.uiSettings?.primaryUnits || {};
        const programMode = useStateSelector("model.modelData.program.class") || "";
        const { id: yearBuiltId, value: yearBuiltValue } =
            useStateSelector("model.modelData.specifications.yearBuilt") || {};

        const { codeRef: selectedCodeRef } = useStateSelector(`${accessor}.ceilingInsType`) || {};
        const selectedNomRValue = useStateSelector(`${accessor}.ceilingInsType_nomRVal`) || 0;
        const ceilingCodeWarning = useStateSelector(`${accessor}.ceilingInsType_warning`) || "";
        const rValUnits = useStateSelector(`${accessor}.ceilingInsType_u`);
        const { [id]: { ceilingInsType: isCalculatingRValue = false } = {} } = calculatingRValue;

        //Determine if selectedCodeRef is in modelCodes (attached properly)
        const modelCodesContainsSelected =
            Object.keys(modelCodes).includes(selectedCodeRef) || selectedCodeRef === "UserSpecified" || isTouched;

        const prebuiltCeilingCodes = isCommunity ? extractPrebuiltEcmCodes(upgradeType) : {};

        //always get from parent
        const { id: constructTypeId = 0 } = useStateSelector(`${componentAccessor}.constructionType`) || {};

        //Check order: 1) model via component accessor, 2) upgrade package library, 3) parametric ecmInputMap
        const isFlat =
            [2, 3].includes(constructTypeId) ||
            (isUpgradeLibrary && [2, 3].includes(constTypeIdFromPackage)) ||
            (isParametricAnalysis && [2, 3].includes(constTypeIdFromParametric)) ||
            (isCommunity && [2, 3].includes(constTypeIdFromCommunity));

        const isErsAndUSpec =
            selectedCodeRef === "UserSpecified" && (programMode.includes("ERS") || programMode.includes("ONrh"));
        const isRecentVintage = [9, 10].includes(yearBuiltId) || yearBuiltValue >= 1990;

        useEffect(() => {
            if (isParametricAnalysis) {
                change(`${accessor}.constructionType.id`, constTypeIdFromParametric);
            } else if (isCommunity) {
                change(`${accessor}.constructionType.id`, constTypeIdFromCommunity);
            } else if (isUpgradeLibrary) {
                change(`${accessor}.constructionType.id`, constTypeIdFromPackage);
            } else if (isUpgrade) {
                change(`${accessor}.constructionType.id`, constructTypeId);
            }
        }, [
            accessor,
            change,
            isUpgrade,
            isUpgradeLibrary,
            isParametricAnalysis,
            isCommunity,
            constructTypeId,
            constTypeIdFromParametric,
            constTypeIdFromPackage,
            constTypeIdFromCommunity,
        ]);

        const handleTypeChange = useCallback(
            ({ codeRef = "" }) => {
                if (!codeRef) return;
                if (codeRef === "UserSpecified") {
                    change(`${accessor}.ceilingInsType_warning`, "");
                    change(`${accessor}.ceilingInsType_nomRVal`, selectedNomRValue || 0.1);
                    change(`${accessor}.ceilingInsType_effRVal`, selectedNomRValue || 0.1);
                    return;
                }

                const modelMatch = modelCodes[codeRef] || {};
                if (!isEmpty(modelMatch)) {
                    change(`${accessor}.ceilingInsType_nomRVal`, modelMatch.nominalRValue || 0.1);
                    change(`${accessor}.ceilingInsType_effRVal`, modelMatch.nominalRValue || 0.1);
                    change(`${accessor}.ceilingInsType_warning`, modelMatch.warningType || "");
                } else {
                    const libCodeMatch = isFlat ? libCeilingFlatCodes[codeRef] : libCeilingCodes[codeRef];
                    const prebuiltCodeMatch = isCommunity ? prebuiltCeilingCodes[codeRef] : {};

                    const { nominalRValue = 0, warningType = "" } = libCodeMatch || prebuiltCodeMatch || {};

                    change(`${accessor}.ceilingInsType_nomRVal`, nominalRValue || 0.1);
                    change(`${accessor}.ceilingInsType_effRVal`, nominalRValue || 0.1);
                    change(`${accessor}.ceilingInsType_warning`, warningType || "");

                    if (isCommunity || isParametricAnalysis) {
                        setIsTouched(true);
                    } else {
                        change(`model.modelData.codes.${codeRef}`, { ...libCodeMatch, codeRef });
                    }
                }
                //Redundancy
                const codeType = codeRef.split("-")[0];
                change(`model.stashedCodes.${codeType}.${accessor.split(".").slice(-1)[0]}`, codeRef);
            },
            [libCeilingCodes, libCeilingFlatCodes, modelCodes, change, accessor, isCommunity, isParametricAnalysis]
        );

        const handleConstructionChange = useCallback(
            ({ id }) => {
                const isNewSelectionFlat = [2, 3].includes(id);
                if (isFlat !== isNewSelectionFlat) {
                    change(`${accessor}.ceilingInsType`, { codeLabel: "User Specified", codeRef: "UserSpecified" });
                    change(`${accessor}.ceilingInsType_nomRVal`, selectedNomRValue || 0.1);
                    change(`${accessor}.ceilingInsType_effRVal`, selectedNomRValue || 0.1);
                    change(`${accessor}.ceilingInsType_warning`, "");
                }
            },
            [constructTypeId, isFlat]
        );

        const handleEditModelCeilingCode = useCallback(
            async (event, codeRef, codeType) => {
                event.stopPropagation(); //Don't let click event bubble up to parent
                const initCodeType = codeType === "U" ? "udefStandard" : "ceilingInsType";
                const code = modelCodes[codeRef];
                await setInitCode(code, initCodeType);
                updateCodeDrawer({
                    isOpen: true,
                    codeName: "Ceiling",
                    isEditing: true,
                    componentType: "Ceiling",
                    codeType: codeType,
                    fieldAccessor: `${accessor}.ceilingInsType`,
                    componentId: id,
                    id: codeRef,
                    lastEdited: code.lastEdited || "",
                });
            },
            [accessor, id, modelCodes, setInitCode, updateCodeDrawer]
        );

        const handleEditLibCeilingCode = disabledCodeEdit
            ? null
            : useCallback(
                  async (event, codeRef, codeType) => {
                      event.stopPropagation(); //Don't let click event bubble up to parent
                      const initCodeType = codeType === "U" ? "udefStandard" : "ceilingInsType";
                      const code = { ...libCeilingCodes[codeRef], codeRef: codeRef, isLibCode: true };
                      await setInitCode(code, initCodeType);
                      updateCodeDrawer({
                          isOpen: true,
                          codeName: "Ceiling",
                          isEditing: true,
                          componentType: "Ceiling",
                          codeType: codeType,
                          fieldAccessor: `${accessor}.ceilingInsType`,
                          componentId: id,
                          id: codeRef,
                          lastEdited: code.lastEdited || "",
                      });
                  },
                  [accessor, id, libCeilingCodes, setInitCode, updateCodeDrawer]
              );

        const handleEditPrebuiltCeilingCode = isCommunity
            ? useCallback(
                  async (event, codeRef, codeType) => {
                      event.stopPropagation(); //Don't let click event bubble up to parent
                      const initCodeType = codeType === "U" ? "udefStandard" : "ceilingInsType";
                      const code = { ...prebuiltCeilingCodes[codeRef], codeRef: codeRef, isPrebuiltEcmCode: true };
                      await setInitCode(code, initCodeType);
                      updateCodeDrawer({
                          isOpen: true,
                          codeName: "Ceiling",
                          isEditing: true,
                          componentType: "Ceiling",
                          codeType: codeType,
                          fieldAccessor: `${accessor}.ceilingInsType`,
                          componentId: id,
                          id: codeRef,
                          lastEdited: code.lastEdited || "",
                      });
                  },
                  [accessor, id, prebuiltCeilingCodes, setInitCode, updateCodeDrawer]
              )
            : null;

        const handleEditModelCeilingFlatCode = useCallback(
            async (event, codeRef, codeType) => {
                event.stopPropagation(); //Don't let click event bubble up to parent
                const initCodeType = codeType === "U" ? "udefStandard" : "ceilingInsType";
                const code = modelCodes[codeRef];
                await setInitCode(code, initCodeType);
                updateCodeDrawer({
                    isOpen: true,
                    codeName: "Flat Ceiling",
                    isEditing: true,
                    componentType: "CeilingFlat",
                    codeType: codeType,
                    fieldAccessor: `${accessor}.ceilingInsType`,
                    componentId: id,
                    id: codeRef,
                    lastEdited: code.lastEdited || "",
                });
            },
            [accessor, id, modelCodes, setInitCode, updateCodeDrawer]
        );

        const handleEditLibCeilingFlatCode = useCallback(
            async (event, codeRef, codeType) => {
                event.stopPropagation(); //Don't let click event bubble up to parent
                const initCodeType = codeType === "U" ? "udefStandard" : "ceilingInsType";
                const code = { ...libCeilingFlatCodes[codeRef], codeRef: codeRef, isLibCode: true };
                await setInitCode(code, initCodeType);
                updateCodeDrawer({
                    isOpen: true,
                    codeName: "Flat Ceiling",
                    isEditing: true,
                    componentType: "CeilingFlat",
                    codeType: codeType,
                    fieldAccessor: `${accessor}.ceilingInsType`,
                    componentId: id,
                    id: codeRef,
                    lastEdited: code.lastEdited || "",
                });
            },
            [accessor, id, libCeilingFlatCodes, setInitCode, updateCodeDrawer]
        );

        const insTypeModelCodesMap = isFlat
            ? ceilingFlatTypeModelCodesMap(
                  modelCodes,
                  { ...libCeilingFlatCodes, ...prebuiltCeilingCodes },
                  handleEditModelCeilingFlatCode
              )
            : ceilingTypeModelCodesMap(
                  modelCodes,
                  { ...libCeilingCodes, ...prebuiltCeilingCodes },
                  handleEditModelCeilingCode
              );

        const insTypeLibCodesMap = isFlat
            ? ceilingFlatTypeLibCodesMap(libCeilingFlatCodes, handleEditLibCeilingFlatCode)
            : ceilingTypeLibCodesMap(libCeilingCodes, handleEditLibCeilingCode);

        const insTypePrebuiltCodesMap = isCommunity
            ? ceilingTypePrebuiltCodesMap(prebuiltCeilingCodes, handleEditPrebuiltCeilingCode)
            : [];

        const gridTemplate = isCompact ? "1fr 1fr 1fr" : isUpgrade ? "2fr 2fr 1fr" : "31.85% 31.85% 23.25%";

        return (
            <>
                <InputRow gridTemplate={gridTemplate} compact={isCompact}>
                    {/* Modify Field component to only use the accessor path if it is not used in upgradeDrawer */}
                    <Field
                        component={Select}
                        name={`${accessor}.constructionType`}
                        options={getOptions({ fieldKey: "ceilingConstructionType" }).map((option) => ({
                            ...option,
                            disabled: codeRefs.includes(option.value?.codeRef),
                        }))}
                        defaultValue={{ id: 0 }}
                        label="Ceiling Construction Type"
                        placeholder="Choose Ceiling Construction Type"
                        validate={constructionTypeValidation}
                        disabled={isUpgrade || !canBeEditedFromParametric}
                        onChange={handleConstructionChange}
                        setValue={isUpgrade ? { id: constructTypeId } : null} // TODO: Doesn't seem to set anything, value taken from fields.constructionType
                        compact={isCompact}
                    />
                    <Field
                        component={Select}
                        type="number"
                        name={`${accessor}.ceilingInsType`}
                        options={[
                            {
                                label: "User Specified",
                                value: {
                                    codeLabel: "User Specified",
                                    codeRef: "UserSpecified",
                                },
                            },
                            ...insTypeModelCodesMap,
                            // ...insTypePrebuiltCodesMap,
                            ...insTypeLibCodesMap,
                        ].map((option) => ({
                            ...option,
                            disabled: codeRefs.includes(option.value.codeRef),
                        }))}
                        label={isFlat ? "Flat Ceiling Type" : "Ceiling Type"}
                        placeholder={isFlat ? "Choose Flat Ceiling Type" : "Choose Ceiling Type"}
                        searchPlaceholder="Search Ceiling Codes"
                        search={true}
                        footer={() => (
                            <CodeFooter
                                toggleDrawer={(value) =>
                                    updateCodeDrawer({
                                        isOpen: value,
                                        codeName: isFlat ? "Flat Ceiling" : "Ceiling",
                                        isEditing: false,
                                        componentType: isFlat ? "CeilingFlat" : "Ceiling",
                                        codeType: "S",
                                        fieldAccessor: `${accessor}.ceilingInsType`,
                                        componentId: id,
                                    })
                                }
                                setInitCode={setInitCode}
                                codeType="ceilingInsType"
                            />
                        )}
                        className={classes.ceilingType}
                        isLoading={isCalculatingRValue}
                        onChange={handleTypeChange}
                        validate={requiredCodeRef}
                        disabled={!canBeEditedFromParametric}
                        compact={isCompact}
                        warning={!modelCodesContainsSelected ? "codeDataMissing" : null}
                    />
                    {selectedCodeRef === "UserSpecified" ? (
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.ceilingInsType_effRVal`}
                            label={"Effective R-Value"}
                            placeholder={0.0}
                            disabled={selectedCodeRef !== "UserSpecified" || !canBeEditedFromParametric}
                            validate={ceilingTypeValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("ceilingInsType")}
                            change={change}
                            units={{
                                base: getBaseUnits("ceilingInsType", modelUnits),
                                options: getUnitOptions("thermalResistance"),
                                selected: rValUnits,
                                unitType: "thermalResistance",
                                accessor: `${accessor}.ceilingInsType_u`,
                            }}
                            onChange={(value) => {
                                if (selectedCodeRef === "UserSpecified") {
                                    change(`${accessor}.ceilingInsType_nomRVal`, value || 0.1);
                                }
                            }}
                            setValue={selectedNomRValue || 0.1} // selectedNomRValue is updated to equal ceilingInsType_effRVal
                            info={"User specified values are assumed to be effective R-values."}
                            warning={
                                isErsAndUSpec
                                    ? "ersUSpecRVal"
                                    : isRecentVintage && selectedNomRValue === 0
                                      ? "ceilingInsLow1990"
                                      : ""
                            }
                            compact={isCompact}
                        />
                    ) : (
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.ceilingInsType_nomRVal`}
                            label="Nominal R-Value"
                            placeholder={0.0}
                            disabled={true}
                            validate={ceilingTypeValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("ceilingInsType")}
                            change={change}
                            setValue={selectedNomRValue || 0.1}
                            units={{
                                base: getBaseUnits("ceilingInsType", modelUnits),
                                options: getUnitOptions("thermalResistance"),
                                selected: rValUnits,
                                unitType: "thermalResistance",
                                accessor: `${accessor}.ceilingInsType_u`,
                            }}
                            info={
                                "Values calculated by the Code Selector are nominal R-values. HOT2000 re-calculates R-values based on the content of the code when running a simulation."
                            }
                            warning={ceilingCodeWarning}
                            isLoading={isCalculatingRValue}
                            compact={isCompact}
                        />
                    )}
                </InputRow>
                {isParametricAnalysis && (
                    <>
                        <InputRow gridTemplate="1fr" compact lastRow>
                            <Field
                                className={classes.bottomRowCheckbox}
                                component={Checkbox}
                                name={`${accessor}.addUserSpecifiedRVal`}
                                label="Add User Specified R-Value to All Components"
                                large
                                type="checkbox"
                                compact
                                onChange={(value) => {
                                    if (value) {
                                        change(`${accessor}.ceilingInsType`, {
                                            codeRef: "UserSpecified",
                                            codeLabel: "User Specified",
                                        });
                                    }
                                }}
                                disabled={!canBeEditedFromParametric}
                            />
                        </InputRow>
                        {isAddUserSpecifiedRVal && (
                            <div
                                style={{
                                    backgroundColor: "aliceblue",
                                    borderRadius: "10px",
                                    padding: "0.75rem",
                                    lineHeight: "normal",
                                    fontSize: "small",
                                    marginTop: "1.25rem",
                                }}
                            >
                                This user specified effective R-value will be added to all attic ceiling components in
                                the model individually. The resulting attic ceiling R-values may not be equal, depending
                                on their starting points.
                                <br />
                                <br />
                                The initial effective R-value for each component is determined using the parallel path
                                calculation method, aligned with F280 and the National Building Code, and may differ
                                slightly from HOT2000's calculation method.
                            </div>
                        )}
                    </>
                )}
            </>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    setInitCode: async (code, type) => {
        return dispatch(setInitCode(code, type));
    },
    updateCodeDrawer: (updates) => dispatch(updateCodeDrawer({ updates })),
    change: (path, value) => dispatch(change(path, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Construction);
